import { useFrame, useThree } from '@react-three/fiber';
import React, {
  type MutableRefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type PointLight,
  type Texture,
  Box3,
  BufferAttribute,
  BufferGeometry,
  Group,
  InstancedBufferAttribute,
  PlaneGeometry,
  ShaderMaterial,
  Sphere,
  TextureLoader,
  Vector3,
  Vector4,
} from 'three';

import { usePromiseStore } from 'state/promiseStore';
import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';

import fragmentShader from './quad-cloud.frag';
import vertexShader from './quad-cloud.vert';
import rainFragmentShader from './rain.frag';
import rainVertexShader from './rain.vert';

const CLOUD_URL = {
  blue: publicAssets('/cloud-blue4.png'),
  white: publicAssets('/cloud.png'),
  bushfire: publicAssets('/cloud-bushfire.png'),
};

const MAX_LIGHTS = 24; // Update the value also in the clouds.frag file
const textureLoader = new TextureLoader();

export interface QuadCloudRef {
  position: Vector3;
  scale: number;
  opacity: number;
  userData: Record<string, any>;
  isLoaded: boolean;
}

export interface QuadCloudProps extends GroupProps {
  /** How many segments or particles the cloud will have, default: 10 */
  segmentCount?: number;
  /** The box3 bounds of the cloud, default: (3, 3, 3) */
  bounds?: Vector3;
  /** The volume/thickness of the segments, default: 6 */
  volume?: number;
  /** Opacity, default: 1 */
  opacity?: number;
  pointLights?: MutableRefObject<PointLight>[];
  baseLight?: number;
  rainHeight?: number;
  rainDensity?: number;
  cloudStyle?: 'blue' | 'white' | 'bushfire';
}

export const QuadCloud = forwardRef<QuadCloudRef, QuadCloudProps>(
  (
    {
      segmentCount = 10,
      bounds = new Vector3(3, 3, 3),
      volume = 3,
      opacity = 1,
      pointLights = [],
      baseLight = 1,
      rainHeight,
      rainDensity = 0.15,
      cloudStyle = 'blue',
      ...props
    },
    ref
  ) => {
    const exec = usePromiseStore(s => s.exec);
    const gl = useThree(state => state.gl);
    const groupRef = useRef<Group>(null!);
    const materialRef = useRef<ShaderMaterial>(null!);
    const rainMaterialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader: rainVertexShader,
        fragmentShader: rainFragmentShader,
        uniforms: {
          uSize: { value: 6 },
          uFallHeight: { value: rainHeight },
          uTime: { value: 0 },
          uColor: { value: new Vector4(0.6, 0.6, 0.6, 0.1) },
        },
        transparent: true,
        depthWrite: false,
      })
    );

    const [texture, setTexture] = useState<Texture | null>(null);

    useEffect(() => {
      exec(CLOUD_URL[cloudStyle], () =>
        textureLoader.loadAsync(CLOUD_URL[cloudStyle])
      ).then(tex => {
        setTexture(tex);
      });
    }, [exec, cloudStyle]);

    const isLoadedRef = useRef(false);

    useEffect(() => {
      isLoadedRef.current = !!texture;
    }, [texture]);

    useImperativeHandle(
      ref,
      () => ({
        position: groupRef.current.position,
        userData: groupRef.current.userData,
        set scale(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uScale.value = n;
        },
        get scale() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uScale.value;
        },
        set opacity(n: number) {
          const mat = materialRef.current;
          if (!mat) return;
          mat.uniforms.uOpacity.value = n;
        },
        get opacity() {
          const mat = materialRef.current;
          if (!mat) return 0;
          return mat.uniforms.uOpacity.value;
        },
        get isLoaded() {
          return isLoadedRef.current;
        },
      }),
      []
    );

    const geometry = useMemo(() => {
      const pg = new PlaneGeometry(2, 2);

      pg.boundingSphere = new Sphere(
        new Vector3(0, 0, 0),
        Math.max(bounds.x, bounds.y, bounds.z) + volume / 2
      );

      const offsets = new Float32Array(segmentCount * 3);
      const sizes = new Float32Array(segmentCount);

      for (let i = 0; i < segmentCount; i++) {
        const i3 = i * 3;
        offsets[i3] = bounds.x * (Math.random() * 2 - 1);
        offsets[i3 + 1] = bounds.y * (Math.random() * 2 - 1);
        offsets[i3 + 2] = bounds.z * (Math.random() * 2 - 1);
        sizes[i] = Math.max(0.2, Math.random()) * volume * gl.getPixelRatio();
      }

      pg.setAttribute('aOffset', new InstancedBufferAttribute(offsets, 3));
      pg.setAttribute('aSize', new InstancedBufferAttribute(sizes, 1));
      return pg;
    }, [gl, segmentCount, bounds, volume]);

    const rainGeometry = useMemo(() => {
      if (!rainHeight) return null;

      const bg = new BufferGeometry();

      const boundingMin = new Vector3(-bounds.x, -rainHeight, -bounds.z);
      const boundingMax = new Vector3(bounds.x, 0, bounds.z);
      bg.boundingBox = new Box3(boundingMin, boundingMax);

      const sizeX = Math.abs(bounds.x * 2);
      const sizeZ = Math.abs(bounds.z * 2);
      const dropColCount = Math.floor(sizeX * rainDensity);
      const dropRowCount = Math.floor(sizeZ * rainDensity);

      const positions = new Float32Array(dropColCount * dropRowCount * 3);
      const yOffsets = new Float32Array(dropColCount * dropRowCount);

      const stepX = sizeX / (dropColCount - 1);
      const stepZ = sizeZ / (dropRowCount - 1);

      for (let i = 0; i < dropColCount; i++) {
        for (let j = 0; j < dropRowCount; j++) {
          const idx = i * dropRowCount + j;
          const idx3 = idx * 3;

          const xPos = -bounds.x + i * stepX;
          const yPos = Math.random() * -rainHeight;
          const zPos = -bounds.z + j * stepZ;

          yOffsets[idx] = yPos;
          positions[idx3] = xPos;
          positions[idx3 + 1] = yPos;
          positions[idx3 + 2] = zPos;
        }
      }

      bg.setAttribute('position', new BufferAttribute(positions, 3));
      bg.setAttribute('aOffsetY', new BufferAttribute(yOffsets, 1));
      return bg;
    }, [bounds, rainHeight, rainDensity]);

    useFrame(({ clock }) => {
      rainMaterialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    });

    useFrame(() => {
      if (!materialRef.current) return;

      for (let i = 0; i < Math.min(pointLights.length, MAX_LIGHTS); i++) {
        const mat = materialRef.current;
        const { x: lx, y: ly, z: lz } = pointLights[i].current.position;
        const intensity = pointLights[i].current.intensity;

        const i3 = i * 3;
        mat.uniforms.uLightPositions.value[i3] = lx;
        mat.uniforms.uLightPositions.value[i3 + 1] = ly;
        mat.uniforms.uLightPositions.value[i3 + 2] = lz;
        mat.uniforms.uLightIntensities.value[i] = intensity;
      }
    });

    return (
      <group ref={groupRef} {...props}>
        {texture && (
          <instancedMesh
            args={[undefined, undefined, segmentCount]}
            geometry={geometry}
            layers={0}
          >
            <shaderMaterial
              ref={materialRef}
              vertexShader={vertexShader}
              fragmentShader={fragmentShader}
              uniforms={{
                uTexture: { value: texture },
                uOpacity: { value: opacity },
                uBaseLight: { value: baseLight },
                uLightPositions: { value: new Float32Array(MAX_LIGHTS * 3) },
                uLightIntensities: { value: new Float32Array(MAX_LIGHTS) },
                uNumLights: {
                  value: Math.min(pointLights.length, MAX_LIGHTS),
                },
                uScale: { value: 1 },
              }}
              transparent={true}
              depthWrite={false}
            />
          </instancedMesh>
        )}
        {rainGeometry && (
          <points geometry={rainGeometry} material={rainMaterialRef.current} />
        )}
      </group>
    );
  }
);
QuadCloud.displayName = 'QuadCloud';
